import React from 'react';
import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {FieldDefinition} from '../../../shared/fields/types';

// Reusable table component for all field types
export const FieldsTable = <T extends FieldDefinition>({
  fieldDefinitions,
  columns,
  getGroupKey,
}: {
  fieldDefinitions: T[];
  columns: ColumnDef<T>[];
  getGroupKey?: (field: T) => string;
}) => {
  const table = useReactTable({
    columns,
    data: fieldDefinitions,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
    defaultColumn: {
      minSize: 0,
      size: 150,
    },
  });

  return (
    <TableContainer border="1px" borderColor="kgray.200" borderRadius="md">
      <Table variant="simple">
        <Thead bg="kgray.100">
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  w={header.getSize() ? `${header.getSize()}px` : 'auto'}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {(() => {
            let lastGroupKey: string | undefined = undefined;
            let groupIndex = 0;

            return table.getRowModel().rows.map((row) => {
              if (getGroupKey) {
                const currentGroupKey = getGroupKey(row.original);
                if (currentGroupKey !== lastGroupKey) {
                  groupIndex++;
                  lastGroupKey = currentGroupKey;
                }
              }

              const bgColor = getGroupKey
                ? groupIndex % 2 === 1
                  ? 'white'
                  : 'kgray.100'
                : undefined;

              return (
                <Tr key={row.id} role="group" bg={bgColor}>
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      _groupHover={{bg: 'kgray.200'}}
                      w={
                        cell.getContext().column.getSize()
                          ? `${cell.getContext().column.getSize()}px`
                          : 'auto'
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </Tr>
              );
            });
          })()}
        </Tbody>
      </Table>
    </TableContainer>
  );
};
